import { useEffect, useState } from 'react';
import { apiClient, ApiError, type ProjectNote, type MemberRow } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { NoteEditor } from './NoteEditor';
import { MarkdownView } from '@/lib/markdown';
import { RelativeTime, DayDivider, dayKey } from '@/components/RelativeTime';
import { UserAvatar } from '@/components/UserAvatar';

interface Props {
  projectId: string;
  myUserId: string;
  /** Project members — used to resolve note authors' avatars. */
  members: MemberRow[];
}

/**
 * Project notes — a collaborative log of longer-form notes under each project.
 * Any member can add; author or project owner/admin can delete.
 *
 * Polling: 10s while tab is visible (less chatty than chat at 5s — notes are
 * not real-time). Optimistic add with rollback.
 */
export function ProjectNotes({ projectId, myUserId, members }: Props) {
  const [notes, setNotes] = useState<ProjectNote[] | null>(null);
  const [myRole, setMyRole] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const canModerate = myRole === 'owner' || myRole === 'admin';

  async function refresh(silent = false) {
    try {
      const r = await apiClient.listProjectNotes(projectId);
      setNotes(r.notes);
      setMyRole(r.my_role);
      if (!silent) setErr(null);
    } catch (e) {
      if (!silent) setErr(e instanceof ApiError ? e.message : 'Failed to load notes');
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true);
    }, 10000);
    function onVis() {
      if (document.visibilityState === 'visible') refresh(true);
    }
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function add() {
    // Trim trailing whitespace but keep newlines/markdown structure intact.
    const body = draft.replace(/\s+$/, '');
    if (!body) return;
    setSending(true);
    setErr(null);
    const tempId = `temp-${Date.now()}`;
    const optimistic: ProjectNote = {
      note_id: tempId,
      user_id: myUserId,
      user_email: 'you',
      user_name: null,
      body_md: body,
      created_at: Date.now(),
      updated_at: Date.now(),
    };
    setNotes((n) => (n ? [...n, optimistic] : [optimistic]));
    setDraft('');
    try {
      const r = await apiClient.createProjectNote(projectId, body);
      setNotes((n) => (n ? n.map((x) => (x.note_id === tempId ? r.note : x)) : n));
    } catch (e) {
      setNotes((n) => (n ? n.filter((x) => x.note_id !== tempId) : n));
      setDraft(body);
      setErr(e instanceof ApiError ? e.message : 'Add failed');
    } finally {
      setSending(false);
    }
  }

  async function destroy(id: string) {
    if (pendingDelete !== id) {
      setPendingDelete(id);
      setTimeout(() => {
        setPendingDelete((curr) => (curr === id ? null : curr));
      }, 3000);
      return;
    }
    setPendingDelete(null);
    try {
      await apiClient.deleteProjectNote(id);
      setNotes((n) => (n ? n.filter((x) => x.note_id !== id) : n));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Delete failed');
    }
  }

  function startEdit(n: ProjectNote) {
    setEditingId(n.note_id);
    setEditDraft(n.body_md);
  }
  function cancelEdit() {
    setEditingId(null);
    setEditDraft('');
  }
  async function commitEdit(id: string) {
    const next = editDraft.trim();
    const original = notes?.find((x) => x.note_id === id);
    if (!original || !next || next === original.body_md) {
      cancelEdit();
      return;
    }
    setNotes((ns) =>
      ns
        ? ns.map((x) => (x.note_id === id ? { ...x, body_md: next, updated_at: Date.now() } : x))
        : ns,
    );
    cancelEdit();
    try {
      await apiClient.editProjectNote(id, next);
    } catch (e) {
      setNotes((ns) =>
        ns
          ? ns.map((x) =>
              x.note_id === id
                ? { ...x, body_md: original.body_md, updated_at: original.updated_at }
                : x,
            )
          : ns,
      );
      setErr(e instanceof ApiError ? e.message : 'Edit failed');
    }
  }

  return (
    <section
      aria-labelledby="project-notes-heading"
      className="section-glow rounded-lg bg-card/30"
    >
      <header className="flex items-center justify-between border-b border-border/60 px-4 py-2">
        <h2
          id="project-notes-heading"
          className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
        >
          Notes
        </h2>
        <span className="font-mono text-[10px] tabular-nums text-muted-foreground/60">
          {notes?.length ?? 0}
        </span>
      </header>

      <div className="divide-y divide-border/40">
        {notes === null ? (
          <div className="space-y-2 p-3" aria-busy="true">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-background/40" />
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-muted-foreground/70">
            No notes yet. Add the first one below.
          </div>
        ) : (
          notes.map((n, ni) => {
            const isMine = n.user_id === myUserId;
            const isTemp = n.note_id.startsWith('temp-');
            const canDelete = !isTemp && (isMine || canModerate);
            const wasEdited = n.updated_at > n.created_at + 1000;
            const isEditing = editingId === n.note_id;
            const prev = ni > 0 ? notes[ni - 1] : null;
            const showDivider = !prev || dayKey(prev.created_at) !== dayKey(n.created_at);
            return (
              <div key={n.note_id}>
                {showDivider && (
                  <div className="px-4 pt-3 pb-1">
                    <DayDivider ts={n.created_at} />
                  </div>
                )}
              <article className="group/note px-4 py-3">
                <div className="mb-1 flex items-center gap-2 text-xs">
                  {(() => {
                    const m = members.find((x) => x.user_id === n.user_id);
                    return (
                      <UserAvatar
                        email={n.user_email}
                        image_key={m?.image_key}
                        avatar_url={m?.avatar_url}
                        size={20}
                        title={n.user_email}
                      />
                    );
                  })()}
                  <span className="truncate font-semibold text-foreground">
                    {n.user_name || n.user_email}
                  </span>
                  <RelativeTime ts={n.created_at} mode="time" />
                  {wasEdited && (
                    <span
                      className="font-mono text-[10px] text-muted-foreground/60"
                      title={`Edited ${new Date(n.updated_at).toLocaleString()}`}
                    >
                      (edited)
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover/note:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
                    {isMine && !isTemp && !isEditing && (
                      <button
                        type="button"
                        onClick={() => startEdit(n)}
                        className="rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground/60 transition-colors hover:text-foreground"
                        title="Edit"
                      >
                        edit
                      </button>
                    )}
                    {canDelete && !isEditing && (
                      <button
                        type="button"
                        onClick={() => destroy(n.note_id)}
                        className={cn(
                          'rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest transition-colors',
                          pendingDelete === n.note_id
                            ? 'border border-destructive/70 bg-destructive/15 text-destructive'
                            : 'text-muted-foreground/60 hover:text-destructive',
                        )}
                        title={
                          pendingDelete === n.note_id
                            ? 'Click again to confirm'
                            : !isMine
                              ? 'Delete (admin)'
                              : 'Delete'
                        }
                      >
                        {pendingDelete === n.note_id ? 'confirm' : 'del'}
                      </button>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="space-y-1">
                    <NoteEditor
                      initialMarkdown={n.body_md}
                      onChange={setEditDraft}
                      autoFocus
                      minHeightPx={80}
                    />
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
                      <button
                        type="button"
                        onClick={() => commitEdit(n.note_id)}
                        className="rounded bg-primary px-2 py-0.5 text-primary-foreground hover:opacity-90"
                      >
                        save
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="rounded px-2 py-0.5 hover:text-foreground"
                      >
                        cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <MarkdownView source={n.body_md} className="break-words text-foreground" />
                )}
              </article>
              </div>
            );
          })
        )}
      </div>

      {err && (
        <div
          role="alert"
          className="border-t border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-foreground"
        >
          {err}
        </div>
      )}

      <div className="border-t border-border/60 px-3 py-2">
        {/* Composer key forces a fresh editor (clears content) after a successful
            send — TipTap editor's content is held internally, so without a remount
            the previous note text would persist after submit. */}
        <NoteEditor
          key={`composer-${notes?.length ?? 0}`}
          initialMarkdown=""
          placeholder="Add a note…"
          onChange={setDraft}
          disabled={sending}
          minHeightPx={64}
        />
        <div className="mt-1 flex items-center justify-end gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">
            Markdown · ⌘/Ctrl+B bold · ⌘I italic
          </span>
          <button
            type="button"
            onClick={add}
            disabled={sending || !draft.trim()}
            className="rounded-md bg-primary px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? 'saving…' : 'add note'}
          </button>
        </div>
      </div>
    </section>
  );
}

function stringHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

